/**
 * Floating format toolbar over text selections — the WYSIWYG affordance on
 * top of live preview. Every button writes plain markdown via the same
 * commands the keyboard uses.
 */
import { showTooltip, type Tooltip, EditorView } from "@codemirror/view";
import { StateField, type EditorState } from "@codemirror/state";
import type { Command } from "@codemirror/view";
import { toggleWrap, insertLink, setHeading } from "./commands";

interface ToolButton {
  label: string;
  title: string;
  cls?: string;
  run: Command;
}

const BUTTONS: ToolButton[] = [
  { label: "B", title: "Bold (Ctrl+B)", cls: "b", run: toggleWrap("**") },
  { label: "I", title: "Italic (Ctrl+I)", cls: "i", run: toggleWrap("*") },
  { label: "S", title: "Strikethrough", cls: "s", run: toggleWrap("~~") },
  { label: "◌", title: "Highlight", cls: "hl", run: toggleWrap("==") },
  { label: "<>", title: "Inline code", cls: "code", run: toggleWrap("`") },
  { label: "🔗", title: "Link (Ctrl+K)", run: insertLink },
  { label: "H1", title: "Heading 1", run: setHeading(1) },
  { label: "H2", title: "Heading 2", run: setHeading(2) },
  { label: "H3", title: "Heading 3", run: setHeading(3) },
];

function selectionTooltip(state: EditorState): Tooltip | null {
  const range = state.selection.main;
  if (range.empty) return null;
  // multi-line block selections get the toolbar too, anchored at the start
  return {
    pos: Math.min(range.from, range.to),
    above: true,
    strictSide: false,
    arrow: false,
    create: (view: EditorView) => {
      const dom = document.createElement("div");
      dom.className = "hn-seltoolbar";
      for (const btn of BUTTONS) {
        const el = document.createElement("button");
        el.type = "button";
        el.textContent = btn.label;
        el.title = btn.title;
        if (btn.cls) el.classList.add(`hn-st-${btn.cls}`);
        el.addEventListener("mousedown", (e) => {
          e.preventDefault(); // keep the editor selection alive
          btn.run(view);
        });
        dom.appendChild(el);
      }
      return { dom, offset: { x: 0, y: 6 } };
    },
  };
}

const selectionToolbarField = StateField.define<Tooltip | null>({
  create: selectionTooltip,
  update(value, tr) {
    if (!tr.docChanged && !tr.selection) return value;
    return selectionTooltip(tr.state);
  },
  provide: (f) => showTooltip.from(f),
});

export const selectionToolbar = [
  selectionToolbarField,
  EditorView.theme({
    ".hn-seltoolbar": {
      display: "flex",
      gap: "2px",
      background: "var(--surface-raised)",
      borderRadius: "var(--radius-md)",
      boxShadow: "var(--shadow-menu)",
      padding: "3px",
    },
    ".hn-seltoolbar button": {
      minWidth: "24px",
      height: "24px",
      padding: "0 6px",
      borderRadius: "var(--radius-xs)",
      background: "transparent",
      border: "none",
      color: "var(--text-secondary)",
      fontSize: "12px",
      fontFamily: "var(--font-ui)",
      fontWeight: "600",
      cursor: "pointer",
      lineHeight: "1",
    },
    ".hn-seltoolbar button:hover": {
      background: "var(--surface-hover)",
      color: "var(--text-primary)",
    },
    ".hn-st-b": { fontWeight: "800" },
    ".hn-st-i": { fontStyle: "italic" },
    ".hn-st-s": { textDecoration: "line-through" },
    ".hn-st-code": { fontFamily: "var(--font-mono)", fontSize: "10px" },
    ".hn-st-hl": { color: "var(--accent-text)" },
  }),
];
